import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import AdUnit from '../components/AdUnit';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

// Get the AdSense publisher ID from environment variables
const ADSENSE_PUB_ID = process.env.REACT_APP_ADSENSE_PUBLISHER_ID || 'ca-pub-XXXXXXXXXXXXXXXX';

// Get site domain from environment variables
const SITE_DOMAIN = process.env.REACT_APP_SITE_DOMAIN || 'https://yourjsontools.com';

const HomePage = () => {
  const location = useLocation();
  const currentPath = location.pathname;

  const isActive = (path) => {
    return currentPath === path;
  };

  // Load Iconify script and CSS
  React.useEffect(() => {
    // Add Iconify CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://code.iconify.design/iconify-icon/1.0.7/iconify-icon.css';
    document.head.appendChild(link);
    
    // Add Iconify script
    const script = document.createElement('script');
    script.src = 'https://code.iconify.design/2/2.2.1/iconify.min.js';
    script.async = true;
    document.head.appendChild(script);
    
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  // JSON-LD structured data for rich search results
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "JSON Tools",
    "alternateName": "yourjsontools.com",
    "url": SITE_DOMAIN,
    "applicationCategory": "DeveloperApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "A comprehensive collection of free online tools for working with JSON data, HTML, and text processing including JSON Beautifier, HTML Viewer, HTML Beautifier, C# to JSON Converter, Schema Validator, Character Counter, and more.",
    "screenshot": SITE_DOMAIN + "/screenshot.png",
    "featureList": "JSON Beautifier, JSON String Converter, JSON Schema Generator, Schema Validator, JSON Path Finder, JSON Compare, C# to JSON, HTML Beautifier, HTML Viewer, Character Count, X Counter",
    "inLanguage": "en",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "214"
    },
    "author": {
      "@type": "Organization",
      "name": "JSON Tools",
      "url": SITE_DOMAIN
    },
    "publisher": {
      "@type": "Organization",
      "name": "JSON Tools",
      "logo": {
        "@type": "ImageObject",
        "url": SITE_DOMAIN + "/logo192.png",
        "width": "192",
        "height": "192"
      }
    },
    "keywords": "JSON tools, JSON beautifier, JSON formatter, JSON schema generator, JSON validator, JSON path finder, JSON compare, HTML beautifier, HTML viewer, C# to JSON, Character Count, X Counter, online tools"
  };

  const toolCategories = [
    {
      category: "JSON Tools",
      color: "#3b82f6", // blue-500
      backgroundColor: "#eff6ff", // blue-50
      tools: [
        { path: '/json-beautifier', name: 'JSON Beautifier', description: 'Format and pretty-print messy JSON data with proper indentation', icon: 'mdi:code-json' },
        { path: '/json-escape-unescape', name: 'JSON Escape Unescape', description: 'Convert between JSON strings and objects seamlessly', icon: 'mdi:swap-horizontal' },
        { path: '/json-compare', name: 'JSON Compare', description: 'Compare two JSON objects and highlight differences', icon: 'mdi:compare' },
        { path: '/json-path-finder', name: 'JSON Path Finder', description: 'Find and extract values using JSONPath expressions', icon: 'mdi:file-search' }
      ]
    },
    {
      category: "JSON Schema",
      color: "#10b981", // emerald-500
      backgroundColor: "#ecfdf5", // emerald-50
      tools: [
        { path: '/json-to-schema', name: 'JSON to Schema Generator', description: 'Generate JSON schema from sample data automatically', icon: 'mdi:file-document-outline' },
        { path: '/schema-validator', name: 'Schema Validator', description: 'Validate JSON data against defined schemas', icon: 'mdi:check-circle' }
      ]
    },
    {
      category: "HTML Tools",
      color: "#f59e0b", // amber-500
      backgroundColor: "#fffbeb", // amber-50
      tools: [
        { path: '/html-beautifier', name: 'HTML Beautifier', description: 'Format and indent HTML code for better readability', icon: 'mdi:language-html5' },
        { path: '/html-viewer', name: 'HTML Viewer', description: 'Live preview of HTML code rendering in real-time', icon: 'mdi:eye' },
        { path: '/markdown-viewer', name: 'Markdown Viewer', description: 'Live Markdown preview with GitHub-flavored Markdown support', icon: 'mdi:language-markdown' }
      ]
    },
    {
      category: "Developer Tools",
      color: "#8b5cf6", // violet-500
      backgroundColor: "#f5f3ff", // violet-50
      tools: [
        { path: '/http-status-checker', name: 'HTTP Status Checker', description: 'Complete reference for HTTP status codes and meanings', icon: 'mdi:web' },
        { path: '/qr-code-generator', name: 'QR Code Generator', description: 'Create custom QR codes with various styling options', icon: 'mdi:qrcode' },
        { path: '/regex-tester', name: 'Regex Tester', description: 'Test and debug regular expressions with live matching', icon: 'mdi:regex' },
        { path: '/text-diff-checker', name: 'Text Diff Checker', description: 'Compare texts and highlight differences side by side', icon: 'mdi:file-compare' },
        { path: '/text-replacer', name: 'Text Replacer', description: 'Find and replace text patterns with regex support', icon: 'mdi:find-replace' },
        { path: '/javascript-compressor', name: 'JavaScript Compressor', description: 'Compress and minify JavaScript code to reduce file size', icon: 'mdi:language-javascript' },
        { path: '/css-compressor', name: 'CSS Compressor', description: 'Minify and beautify CSS, SASS, LESS files online for free', icon: 'mdi:language-css3' },
        { path: '/jwt-decoder', name: 'JWT Decoder', description: 'Decode, verify, and generate JSON Web Tokens (JWT)', icon: 'mdi:key-variant' }
      ]
    },
    {
      category: "Basic Tools",
      color: "#ef4444", // red-500
      backgroundColor: "#fef2f2", // red-50
      tools: [
        { path: '/notepad', name: 'Online Notepad', description: 'Simple and clean text editor with save, print and formatting options', icon: 'mdi:notebook-edit' },
        { path: '/lorem-ipsum', name: 'Lorem Ipsum', description: 'Placeholder text for your designs and mockups with multiple languages', icon: 'mdi:text' },
        { path: '/csharp-to-json', name: 'C# Class to JSON', description: 'Convert C# class definitions to JSON format', icon: 'mdi:microsoft-visual-studio-code' },
        { path: '/letter-count', name: 'Letter Count', description: 'Count characters, words, sentences and paragraphs', icon: 'mdi:counter' },
        { path: '/x-counter', name: 'X Counter', description: 'Count specific characters or patterns in text', icon: 'mdi:calculator' },
        { path: '/dummy-files', name: 'Dummy Files Generator', description: 'Generate test images and PDF files for development', icon: 'mdi:file-multiple' }
      ]
    },
    {
      category: "AI Tools",
      color: "#ec4899", // pink-500
      backgroundColor: "#fdf2f8", // pink-50
      tools: [
        { path: '/json-to-toon', name: 'JSON to TOON', description: 'Convert JSON to Token-Oriented Object Notation. Save 30-60% tokens for LLMs', icon: 'mdi:robot' },
        { path: '/template-prompts', name: 'Template Prompts', description: 'Ready-to-use AI prompt templates for coding, writing, and more', icon: 'mdi:robot' }
      ]
    }
  ];

  return (
    <>
      <SEO 
        title="Free Online JSON, HTML & Text Tools for Developers | yourjsontools.com"
        description="Collection of free online JSON, HTML and text processing tools including JSON Beautifier, HTML Viewer, HTML Beautifier, C# to JSON Converter, Schema Generator, Character Counter and more. No installation required."
        keywords={["JSON tools", "JSON beautifier", "JSON formatter", "JSON schema generator", "JSON validator", "JSON path finder", "JSON compare", "HTML beautifier", "HTML viewer", "C# to JSON", "Character Count", "X Counter", "online tools"]}
        canonicalUrl={SITE_DOMAIN}
        structuredData={jsonLd}
      />
      
      <div className="home-container">
        <div className="hero-section">
          <h1>Developer Tools <span className="highlight">Directory</span></h1>
          <p className="hero-tagline">Free online utilities for developers, data analysts, and curious minds</p>
          <div className="stats">
            <span className="stat-item">25 Tools</span>
            <span className="stat-item">6 Categories</span>
            <span className="stat-item">100% Free</span>
          </div>
        </div>
      
        {/* All Tools Grid */}
        <div className="all-tools-section">
          <h2 className="section-title">All Tools</h2>
          <div className="tools-grid-all">
            {toolCategories.map((category) => 
              category.tools.map(tool => (
                <Link key={tool.path} to={tool.path} className={`tool-box ${isActive(tool.path) ? 'active' : ''}`}>
                  <div className="tool-box-icon">
                    <i className={`iconify`} data-icon={tool.icon} style={{ color: category.color }}></i>
                  </div>
                  <div className="tool-box-content">
                    <h3 className="tool-box-title">{tool.name}</h3>
                    <div className="tool-category-badge" style={{ backgroundColor: category.color }}>
                      {category.category}
                    </div>
                    <p className="tool-box-description">{tool.description}</p>
                  </div>
                  <div className="tool-box-footer">
                    <span className="demo-link">Try Now</span>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Categories Section */}
        <div className="categories-section">
          <h2 className="section-title">Browse by Category</h2>
          <div className="categories-grid">
            {toolCategories.map((category, index) => (
              <div key={index} className="category-box">
                <div className="category-header" style={{ background: `linear-gradient(135deg, ${category.color} 0%, ${category.color} 100%)` }}>
                  <h3 className="category-name">{category.category}</h3>
                  <span className="category-count">{category.tools.length} tools</span>
                </div>
                <div className="category-tools">
                  {category.tools.map(tool => (
                    <Link key={tool.path} to={tool.path} className="category-tool-link">
                      <i className="iconify tool-icon-small" data-icon={tool.icon}></i>
                      <span className="tool-name-small">{tool.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Bottom AdSense using component with named slot */}
      </div>
      
      <style>{`
        .home-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
          background: #f8fafc;
          min-height: 100vh;
        }
        
        .hero-section {
          text-align: center;
          padding: 60px 0 40px;
          margin-bottom: 50px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          margin: 0 -20px 50px -20px;
          color: white;
          position: relative;
          overflow: hidden;
        }
        
        .hero-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="white" opacity="0.1"/><circle cx="75" cy="75" r="1" fill="white" opacity="0.1"/><circle cx="50" cy="10" r="0.5" fill="white" opacity="0.1"/><circle cx="10" cy="60" r="0.5" fill="white" opacity="0.1"/><circle cx="90" cy="40" r="0.5" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
          pointer-events: none;
        }
        
        .hero-section > * {
          position: relative;
          z-index: 1;
        }
        
        .hero-section h1 {
          font-size: 48px;
          margin-bottom: 15px;
          color: white;
          font-weight: 700;
          text-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .highlight {
          color: #fbbf24;
          position: relative;
          display: inline-block;
        }
        
        .hero-tagline {
          font-size: 20px;
          color: rgba(255,255,255,0.9);
          margin-bottom: 30px;
          font-weight: 400;
        }
        
        .stats {
          display: flex;
          justify-content: center;
          gap: 30px;
          margin-top: 30px;
        }
        
        .stat-item {
          padding: 12px 20px;
          background: rgba(255,255,255,0.15);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 25px;
          font-size: 14px;
          font-weight: 600;
          color: white;
          transition: all 0.3s ease;
        }
        
        .stat-item:hover {
          background: rgba(255,255,255,0.25);
          transform: translateY(-2px);
        }
        
        .section-title {
          font-size: 32px;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 40px;
          text-align: center;
          position: relative;
        }
        
        .section-title::after {
          content: '';
          position: absolute;
          bottom: -10px;
          left: 50%;
          transform: translateX(-50%);
          width: 60px;
          height: 3px;
          background: linear-gradient(90deg, #3b82f6, #8b5cf6);
          border-radius: 2px;
        }
        
        .all-tools-section {
          margin-bottom: 80px;
          background: white;
          padding: 50px 30px;
          border-radius: 20px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }
        
        .tools-grid-all {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 25px;
          margin-bottom: 40px;
        }
        
        .tool-box {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 24px;
          text-decoration: none;
          color: inherit;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
          display: flex;
          flex-direction: column;
          height: auto;
          min-height: 220px;
          position: relative;
          overflow: hidden;
        }
        
        .tool-box::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #3b82f6, #8b5cf6);
          transform: scaleX(0);
          transition: transform 0.3s ease;
        }
        
        .tool-box:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          border-color: #3b82f6;
        }
        
        .tool-box:hover::before {
          transform: scaleX(1);
        }
        
        .tool-box.active {
          border-color: #3b82f6;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }
        
        .tool-box.active::before {
          transform: scaleX(1);
        }
        
        .tool-box-icon {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 0 16px 0;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 12px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          flex-shrink: 0;
        }
        
        .tool-box-icon i {
          font-size: 24px !important;
          width: 24px !important;
          height: 24px !important;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .tool-box-icon i.iconify {
          font-size: 24px !important;
          width: 24px !important;
          height: 24px !important;
        }
        
        .tool-box:hover .tool-box-icon {
          transform: scale(1.05);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }
        
        .tool-box-content {
          flex: 1;
          text-align: left;
          display: flex;
          flex-direction: column;
        }
        
        .tool-box-title {
          font-size: 18px;
          font-weight: 600;
          color: #111827;
          margin: 0 0 10px 0;
          line-height: 1.3;
        }
        
        .tool-category-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 16px;
          font-size: 10px;
          font-weight: 600;
          color: white;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 10px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.1);
          align-self: flex-start;
        }
        
        .tool-box-description {
          font-size: 14px;
          color: #6b7280;
          margin: 0 0 auto 0;
          line-height: 1.4;
          flex: 1;
        }
        
        .tool-box-footer {
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid #f3f4f6;
        }
        
        .demo-link {
          color: #3b82f6;
          font-size: 14px;
          font-weight: 600;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        
        .demo-link::after {
          content: '→';
          transition: transform 0.2s ease;
        }
        
        .tool-box:hover .demo-link::after {
          transform: translateX(4px);
        }
        
        .categories-section {
          margin-bottom: 80px;
        }
        
        .categories-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 30px;
        }
        
        .category-box {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .category-box:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }
        
        .category-header {
          padding: 25px;
          color: white;
          text-align: center;
          position: relative;
        }
        
        .category-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%);
        }
        
        .category-header > * {
          position: relative;
          z-index: 1;
        }
        
        .category-name {
          font-size: 20px;
          font-weight: 600;
          margin: 0 0 8px 0;
          text-shadow: 0 1px 2px rgba(0,0,0,0.1);
        }
        
        .category-count {
          font-size: 14px;
          opacity: 0.9;
          font-weight: 500;
        }
        
        .category-tools {
          padding: 25px;
        }
        
        .category-tool-link {
          display: flex;
          align-items: center;
          padding: 14px 0;
          text-decoration: none;
          color: #374151;
          border-bottom: 1px solid #f3f4f6;
          transition: all 0.2s ease;
          border-radius: 8px;
          margin: 0 -8px;
          padding-left: 8px;
          padding-right: 8px;
        }
        
        .category-tool-link:last-child {
          border-bottom: none;
        }
        
        .category-tool-link:hover {
          color: #3b82f6;
          background: #f8fafc;
          padding-left: 16px;
        }
        
        .tool-icon-small {
          font-size: 18px;
          margin-right: 14px;
          width: 24px;
          text-align: center;
          color: inherit;
        }
        
        .tool-name-small {
          font-size: 15px;
          font-weight: 500;
        }
        
        @media (max-width: 1200px) {
          .tools-grid-all {
            grid-template-columns: repeat(3, 1fr);
            gap: 20px;
          }
        }
        
        @media (max-width: 768px) {
          .home-container {
            padding: 0 15px;
          }
          
          .hero-section {
            margin: 0 -15px 40px -15px;
            padding: 50px 20px 35px;
          }
          
          .hero-section h1 {
            font-size: 36px;
          }
          
          .hero-tagline {
            font-size: 18px;
          }
          
          .stats {
            flex-direction: column;
            align-items: center;
            gap: 15px;
          }
          
          .all-tools-section {
            padding: 40px 20px;
            margin-bottom: 60px;
          }
          
          .tools-grid-all {
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
          }
          
          .tool-box {
            height: auto;
            min-height: 200px;
            padding: 20px;
          }
          
          .categories-grid {
            grid-template-columns: 1fr;
            gap: 25px;
          }
          
          .section-title {
            font-size: 28px;
          }
        }
        
        @media (max-width: 480px) {
          .tools-grid-all {
            grid-template-columns: 1fr;
          }
          
          .tool-box {
            height: auto;
            min-height: 180px;
            padding: 18px;
          }
          
          .hero-section h1 {
            font-size: 28px;
          }
          
          .hero-section {
            padding: 40px 15px 30px;
          }
          
          .stats {
            gap: 12px;
          }
          
          .stat-item {
            padding: 10px 16px;
            font-size: 13px;
          }
          
          .all-tools-section {
            padding: 30px 15px;
          }
          
          .categories-section {
            margin-bottom: 60px;
          }
        }
      `}</style>
    </>
  );
};

export default HomePage; 